// ABOUTME: Utility functions for chart responsiveness and calculations
// ABOUTME: Provides consistent responsive behavior across all chart components

import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';

/**
 * Chart radius configuration for different breakpoints
 */
export const CHART_RADIUS_CONFIG = {
  desktop: 120,
  tablet: 90,
  mobile: 70
} as const;

/**
 * Avatar size configuration (consistent across all breakpoints)
 */
export const AVATAR_CONFIG = {
  size: 60,
  borderWidth: 3,
  borderColor: 'white'
} as const;

/**
 * System entity configuration
 */
export const SYSTEM_ENTITY_CONFIG = {
  voiceRecordingId: -1,
  voiceRecordingName: 'Voice Recording',
  iconSize: 24
} as const;

/**
 * Calculates responsive pie chart radius based on container width
 * @param containerWidth The width of the chart container in pixels
 * @returns The appropriate radius for the current container size
 */
export const calculateResponsivePieChartRadius = (containerWidth: number): number => {
  // Use smaller percentage for narrow containers to leave room for labels
  const radiusPercentage = containerWidth < 500 ? 0.18 : 0.25;
  const calculatedRadius = Math.floor(containerWidth * radiusPercentage);

  // Set reasonable bounds: min 30px, max 120px
  const minRadius = 30;
  const maxRadius = 120;

  return Math.max(minRadius, Math.min(maxRadius, calculatedRadius));
};

/**
 * Calculates responsive label radius based on container width and chart radius
 * @param chartRadius The outer radius of the pie chart
 * @param containerWidth The width of the chart container
 * @returns The radius for label positioning that fits in container
 */
export const calculateResponsiveLabelRadius = (chartRadius: number, containerWidth: number): number => {
  // For narrow containers, position labels much closer
  if (containerWidth < 500) {
    return chartRadius + 30; // Reduced from 65 for narrow containers
  }
  return chartRadius + 50; // Slightly reduced from 65 for medium containers
};

/**
 * Hook to get responsive pie chart radius based on current breakpoint (legacy)
 * @deprecated Use calculateResponsivePieChartRadius with container width instead
 * @returns The appropriate radius for the current screen size
 */
export const useResponsivePieChartRadius = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme?.breakpoints?.down?.('sm') || '(max-width: 600px)');
  const isTablet = useMediaQuery(theme?.breakpoints?.down?.('md') || '(max-width: 900px)');

  if (isMobile) return CHART_RADIUS_CONFIG.mobile;
  if (isTablet) return CHART_RADIUS_CONFIG.tablet;
  return CHART_RADIUS_CONFIG.desktop;
};

/**
 * Calculates avatar positioning radius based on chart radius
 * @param chartRadius The outer radius of the pie chart
 * @returns The radius at which avatars should be positioned
 */
export const calculateAvatarRadius = (chartRadius: number): number => {
  return chartRadius + 20;
};

/**
 * Calculates label positioning radius based on chart radius
 * @param chartRadius The outer radius of the pie chart
 * @returns The radius at which labels should be positioned
 */
export const calculateLabelRadius = (chartRadius: number): number => {
  return chartRadius + 65;
};

/**
 * Calculates responsive container height based on chart radius
 * @param chartRadius The outer radius of the pie chart
 * @returns The appropriate container height
 */
export const calculateContainerHeight = (chartRadius: number): number => {
  return chartRadius * 2 + 160; // radius * 2 + padding for labels
};

/**
 * Determines if an entity is a system entity (voice recording, etc.)
 * @param entityId The entity ID to check
 * @param isSystemCategory Optional explicit system category flag
 * @returns True if this is a system entity
 */
export const isSystemEntity = (entityId: number | string, isSystemCategory?: boolean): boolean => {
  return entityId === SYSTEM_ENTITY_CONFIG.voiceRecordingId || isSystemCategory === true;
};

/**
 * Calculates mobile-specific label radius for closer positioning
 * @param chartRadius The outer radius of the pie chart
 * @returns The mobile-optimized radius for labels
 */
export const calculateMobileLabelRadius = (chartRadius: number): number => {
  return chartRadius + 45; // Reduced from 65 to bring labels closer
};

/**
 * Returns the mobile-optimized font size for chart labels
 * @returns Smaller font size for mobile devices
 */
export const calculateMobileFontSize = (): number => {
  return 12; // Reduced from 14px for mobile
};

/**
 * Extracts the first name from a full name string
 * @param fullName The complete name string
 * @returns The first name only
 */
export const extractFirstName = (fullName: string): string => {
  if (!fullName || typeof fullName !== 'string') {
    return '';
  }

  const trimmedName = fullName.trim();
  if (!trimmedName) {
    return '';
  }

  return trimmedName.split(' ')[0];
};

/**
 * Hook to determine if mobile layout should be used based on current breakpoint
 * @returns True if mobile layout should be applied
 */
export const useMobileLayout = (): boolean => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme?.breakpoints?.down?.('sm') || '(max-width: 600px)');
  return isMobile;
};